import { useEffect, useState } from 'react';
import { reserveUrl } from '@/lib/apiUrls';
import { mergeReserveActAsHeaders, getReserveActAsUserId } from '@/lib/reserveActAs';

/**
 * Defera Reserve was discontinued for new users. Existing customers with a
 * wallet balance (or funds allocated to an active Reserve trip) keep full
 * access — for everyone else we hide every Reserve UI surface.
 *
 * Eligibility = `balance_cents > 0` OR `total_allocated_cents > 0`. Both
 * fields are already returned by the existing `/api/reserve/balance`
 * endpoint, so no backend changes are required.
 *
 * The result is cached in `sessionStorage` (per Reserve "act-as" identity)
 * for a short window so that gating multiple components on a single page
 * load doesn't fan out into N network calls.
 */

const CACHE_TTL_MS = 60_000;

const cacheKeyFor = (actAsId) =>
  `defera.reserveEligibility.v1${actAsId ? `:${actAsId}` : ''}`;

function readCache(actAsId) {
  try {
    const raw = sessionStorage.getItem(cacheKeyFor(actAsId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.cachedAt !== 'number') return null;
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(actAsId, value) {
  try {
    sessionStorage.setItem(
      cacheKeyFor(actAsId),
      JSON.stringify({ ...value, cachedAt: Date.now() })
    );
  } catch {
    /* ignore */
  }
}

/**
 * Clears the cached eligibility result. Call after actions that could change
 * a user's Reserve balance (e.g. successful top-up, ledger adjustment).
 */
export function invalidateReserveEligibility() {
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('defera.reserveEligibility.v1'))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

export function useReserveEligibility() {
  const actAsId = getReserveActAsUserId();
  const cached = readCache(actAsId);

  const [state, setState] = useState(() =>
    cached
      ? {
          loading: false,
          isEligible: cached.isEligible,
          balanceCents: cached.balanceCents,
          totalAllocatedCents: cached.totalAllocatedCents,
          error: null,
        }
      : {
          loading: true,
          isEligible: false,
          balanceCents: 0,
          totalAllocatedCents: 0,
          error: null,
        }
  );

  useEffect(() => {
    if (cached) return;

    let cancelled = false;

    (async () => {
      try {
        const { supabase } = await import('@/lib/supabase');
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session) {
          const value = {
            isEligible: false,
            balanceCents: 0,
            totalAllocatedCents: 0,
          };
          writeCache(actAsId, value);
          if (!cancelled) setState({ loading: false, ...value, error: null });
          return;
        }

        const res = await fetch(reserveUrl('balance'), {
          headers: mergeReserveActAsHeaders({
            Authorization: `Bearer ${session.access_token}`,
          }),
        });

        if (!res.ok) {
          if (!cancelled) {
            setState({
              loading: false,
              isEligible: false,
              balanceCents: 0,
              totalAllocatedCents: 0,
              error: new Error(`Reserve eligibility check failed (${res.status})`),
            });
          }
          return;
        }

        const data = await res.json();
        const balanceCents = Number(data?.balance_cents) || 0;
        const totalAllocatedCents = Number(data?.total_allocated_cents) || 0;
        const isEligible = balanceCents > 0 || totalAllocatedCents > 0;

        const value = { isEligible, balanceCents, totalAllocatedCents };
        writeCache(actAsId, value);
        if (!cancelled) setState({ loading: false, ...value, error: null });
      } catch (err) {
        if (!cancelled) {
          setState({
            loading: false,
            isEligible: false,
            balanceCents: 0,
            totalAllocatedCents: 0,
            error: err instanceof Error ? err : new Error(String(err)),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [actAsId, cached]);

  return state;
}

export default useReserveEligibility;
